import {
    Briefcase,
    ChartNoAxesCombined,
    Eye,
    FolderTree,
    Image as ImageIcon,
    LucideIcon,
    Megaphone,
    Package,
    Radar,
    Sparkles,
    Target,
    Users,
} from 'lucide-react';

import { CsAgentRun } from '../../../common/models/content-studio.model';

/**
 * Who the vendor's agents are, and how their work reads.
 *
 * One place for an agent's glyph, name and the words for what its tools do, so
 * Home, a conversation, the Agents page and the Work drawer all introduce the
 * same agent the same way. The ids are the server's (`team:<id>` profiles and
 * the roster in pms-assistant/team) — nothing here invents an agent the server
 * does not run.
 */

export type AgentId =
    | 'lead'
    | 'analyst'
    | 'market'
    | 'catalogue'
    | 'sales'
    | 'marketing'
    | 'ads'
    | 'watch'
    | 'business-analyst'
    | 'content-planner'
    | 'categoriser';

const ICONS: Record<AgentId, LucideIcon> = {
    lead: Sparkles,
    analyst: ChartNoAxesCombined,
    market: Radar,
    catalogue: Package,
    sales: Users,
    marketing: Megaphone,
    ads: Target,
    watch: Eye,
    'business-analyst': Briefcase,
    'content-planner': ImageIcon,
    categoriser: FolderTree,
};

const KNOWN = new Set<string>(Object.keys(ICONS));

export const isAgentId = (id: string): id is AgentId => KNOWN.has(id);

export const agentIcon = (id: string): LucideIcon => (isAgentId(id) ? ICONS[id] : Sparkles);

/** The agent a run belongs to: `team:analyst` → analyst, `assistant` → lead. */
export function agentOfProfile(profile: string): AgentId | null {
    if (profile === 'assistant') return 'lead';
    if (profile.startsWith('team:')) {
        const id = profile.slice('team:'.length);
        return isAgentId(id) ? id : null;
    }
    return isAgentId(profile) ? profile : null;
}

export const agentOfRun = (run: Pick<CsAgentRun, 'profile'>): AgentId | null => agentOfProfile(run.profile);

type T = (key: string, opts?: any) => unknown;

/** English names, used as the fallback when a translation is missing. */
const NAMES: Record<AgentId, string> = {
    lead: 'Ardi',
    analyst: 'Performance Analyst',
    market: 'Competitor Watch',
    catalogue: 'Catalogue Auditor',
    sales: 'Sales & Leads',
    marketing: 'Marketing',
    ads: 'Ads',
    watch: 'Watch',
    'business-analyst': 'Business Analyst',
    'content-planner': 'Studio Planner',
    categoriser: 'Catalogue Organiser',
};

const keyOf = (id: string) => id.toUpperCase().replace(/-/g, '_');

export const agentName = (t: T, id: string, fallback?: string): string =>
    String(t(`AGENTS.MEMBER.${keyOf(id)}.NAME`, { defaultValue: fallback ?? (isAgentId(id) ? NAMES[id] : id) }));

export const agentRole = (t: T, id: string, fallback = ''): string =>
    String(t(`AGENTS.MEMBER.${keyOf(id)}.ROLE`, { defaultValue: fallback }));

/** `search_products` → "search products", for a tool nobody has worded yet. */
export const humaniseTool = (tool: string): string => {
    const words = tool.replace(/[_-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/**
 * What a step is doing, said the way a vendor would say it.
 *
 * Showing `list_open_insights` would be showing them our internals. A tool
 * without a label falls back to its name made readable — true of any tool
 * added later and not yet worded, and better than a blank line.
 */
export const toolLabel = (t: T, tool: string): string =>
    String(t(`AGENTS.TOOL.${tool.toUpperCase()}`, { defaultValue: humaniseTool(tool) }));

/**
 * A reply's opening, as one plain line — for a feed or a card, where the
 * markdown the reply was written in would show as asterisks.
 */
export const plainLine = (text: string | undefined | null): string =>
    String(text ?? '')
        .replace(/\*\*|__|`/g, '')
        .replace(/^#{1,6}\s*/gm, '')
        .replace(/^-{3,}\s*$/gm, '')
        .replace(/\s+/g, ' ')
        .trim();

/** "12s", "1m 04s" — how long the turn has been working. */
export function elapsed(ms: number): string {
    const s = Math.max(0, Math.floor(ms / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return `${m}m ${String(s % 60).padStart(2, '0')}s`;
}
